import BaseModel from './BaseModel';
import CronTaskInterval from './CronTaskInterval';
import CronTaskRun from './CronTaskRun';
import CronTaskRunParameter from './CronTaskRunParameter';
import moment from 'moment';
import * as _ from 'es-toolkit';
import { v4 } from 'uuid';

/** Class representing a cron task. */
class CronTask extends BaseModel {
    static DEFAULT_TIMEOUT = 300;

    static STATUS_PENDING     = 'pending';
    static STATUS_IN_PROGRESS = 'in_progress';
    static STATUS_COMPLETED   = 'completed';
    static STATUS_FAILED      = 'failed';

    static METHOD_GET    = 'GET';
    static METHOD_POST   = 'POST';
    static METHOD_PUT    = 'PUT';
    static METHOD_PATCH  = 'PATCH';
    static METHOD_DELETE = 'DELETE';
    static METHOD_HEAD   = 'HEAD';

    static methods() {
        return [
            CronTask.METHOD_GET,
            CronTask.METHOD_POST,
            CronTask.METHOD_PUT,
            CronTask.METHOD_PATCH,
            CronTask.METHOD_DELETE,
            CronTask.METHOD_HEAD
        ];
    }

    constructor(properties) {
        super(properties);

        this.history = (this.history || []).map(interval => new CronTaskInterval(interval));
        this.run_parameters = (this.run_parameters || []).map(param => new CronTaskRunParameter(param));
        this.last_runs = (this.last_runs || []).map(run => new CronTaskRun(run));
        if (this.last_result)
            this.last_result = new CronTaskRun(this.last_result);

        if (this.created_at)
            this.created_at = moment(this.created_at);
        if (this.updated_at)
            this.updated_at = moment(this.updated_at);
        if (this.last_start_at)
            this.last_start_at = moment(this.last_start_at);
        if (this.last_stop_at)
            this.last_stop_at = moment(this.last_stop_at);
    }

    lastResultSummary() {
        return this.last_result?.summaryLine() || null;
    }

    recentRuns() {
        return this.last_runs || [];
    }

    nbExecutions() {
        return (this.history || []).reduce((acc, interval) => acc + interval.nb, 0);
    }

    nbFailedExecutions() {
        return (this.history || [])
            .filter(interval => interval.status === CronTask.STATUS_FAILED)
            .reduce((acc, interval) => acc + interval.nb, 0);
    }

    nbSuccessfulExecutions() {
        return (this.history || [])
            .filter(interval => interval.status === CronTask.STATUS_COMPLETED)
            .reduce((acc, interval) => acc + interval.nb, 0);
    }

    lastHistoryStatus() {
        return this.history?.[0]?.status || null;
    }

    hasRecentFailure() {
        return this.lastHistoryStatus() === CronTask.STATUS_FAILED;
    }

    lastFailedAt() {
        const failedInterval = (this.history || []).find(
            interval => interval.status === CronTask.STATUS_FAILED
        );
        return failedInterval?.to || failedInterval?.from || null;
    }

    runParameters() {
        return this.run_parameters || [];
    }

    hasRunParameters() {
        return this.runParameters().length > 0;
    }

    buildRunBody(runValues = {}, bodyOverride = {}) {
        const baseBody = _.cloneDeep(this.body || {});
        const merged = {
            ...baseBody,
            ...runValues,
            ...bodyOverride,
        };
        return merged;
    }

    acceptsBody() {
        return [
            CronTask.METHOD_POST,
            CronTask.METHOD_PUT,
            CronTask.METHOD_PATCH
        ].includes(this.method);
    }

    isJsonValueEmpty(value) {
        if (value == null || value === '')
            return true;

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed || trimmed === '{}' || trimmed === '[]')
                return true;

            try {
                return this.isJsonValueEmpty(JSON.parse(trimmed));
            } catch {
                return false;
            }
        }

        if (Array.isArray(value))
            return value.length === 0 || value.every(item => this.isJsonValueEmpty(item));

        if (typeof value === 'object')
            return Object.keys(value).length === 0
                || Object.values(value).every(item => this.isJsonValueEmpty(item));

        return false;
    }

    hasRequestBody() {
        return !this.isJsonValueEmpty(this.body);
    }

    /**
     * Prepare this object for duplicate.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForDuplicate() {
        const prepared = _.cloneDeep(this);

        prepared._id   = v4();
        prepared.name += ' copy';

        delete prepared.created_at;
        delete prepared.updated_at;
        delete prepared.status;
        delete prepared.last_result;
        delete prepared.last_runs;
        delete prepared.history;

        return prepared;
    }
}

export default CronTask;
